import {createContext, useContext, useEffect, useMemo, useState} from 'react'

import type {Shutdown, NewShutdown} from '../types'

interface ShutdownsContextState {
  activeShutdowns: Shutdown[]
  currentShutdowns: (Shutdown | NewShutdown)[]
  newShutdown: NewShutdown | null
  setNewShutdown: (value: NewShutdown | null) => void
  selectedShutdown: Shutdown | undefined
  setSelectedShutdown: (value: Shutdown | undefined) => void
}

const ShutdownsContext = createContext<ShutdownsContextState | undefined>(undefined)

export const useShutdowns = () => {
  const context = useContext(ShutdownsContext)
  if (!context) throw new Error('useShutdowns is used outside of the ShutdownsProvider!')
  return context
}

interface ShutdownsProviderProps {
  shutdowns: Shutdown[]
  selectedShutdownId?: string
  children: React.ReactNode
}

export const ShutdownsProvider: React.FC<ShutdownsProviderProps> = ({
  shutdowns,
  children,
  selectedShutdownId
}) => {
  const [newShutdown, setNewShutdown] = useState<NewShutdown | null>(null)

  const preSelectedShutdown = useMemo(
    () => shutdowns.find((shutdown) => shutdown.id === Number(selectedShutdownId)),
    [shutdowns, selectedShutdownId]
  )

  const [selectedShutdown, setSelectedShutdown] = useState<Shutdown | undefined>(
    preSelectedShutdown
  )

  useEffect(() => {
    if (selectedShutdown) {
      setSelectedShutdown(shutdowns.find((shutdown) => shutdown.id === selectedShutdown.id))
    }
  }, [selectedShutdown, shutdowns])

  return (
    <ShutdownsContext.Provider
      value={{
        activeShutdowns: shutdowns,
        currentShutdowns: [...(newShutdown !== null ? [newShutdown] : []), ...shutdowns],
        newShutdown,
        setNewShutdown,
        selectedShutdown,
        setSelectedShutdown
      }}
    >
      {children}
    </ShutdownsContext.Provider>
  )
}
